import { Injectable, ApplicationRef, inject, NgZone } from '@angular/core';
import { first, filter } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class DeferredStarterService {
  private appRef = inject(ApplicationRef);
  private zone = inject(NgZone);

  /**
   * Executa uma tarefa quando a aplicação estiver estável.
   * Ideal para serviços que não interferem visualmente no primeiro render.
   * Possui um fallback de 5 segundos caso a aplicação demore a estabilizar.
   */
  runWhenStable(task: () => void, timeout = 0): void {
    const runTask = () => {
      if (timeout > 0) {
        setTimeout(() => {
          this.zone.runOutsideAngular(() => task());
        }, timeout);
      } else {
        this.zone.runOutsideAngular(() => task());
      }
    };

    // Fallback: se em 5 segundos não estabilizar, executa assim mesmo
    const stabilityTimeout = setTimeout(() => {
      runTask();
    }, 5000);

    this.appRef.isStable
      .pipe(
        filter((stable) => stable),
        first(),
      )
      .subscribe(() => {
        clearTimeout(stabilityTimeout);
        runTask();
      });
  }

  /**
   * Executa uma tarefa quando o navegador estiver ocioso (requestIdleCallback).
   * Ideal para scripts de terceiros, analytics e limpezas de memória.
   */
  runWhenIdle(task: () => void, timeout = 5000): void {
    if ('requestIdleCallback' in window) {
      (window as any).requestIdleCallback(
        () => {
          this.zone.runOutsideAngular(() => task());
        },
        { timeout },
      );
    } else {
      // Fallback para setTimeout se requestIdleCallback não estiver disponível
      this.runWhenStable(task, 2000);
    }
  }
}
